import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, X, CheckCheck, Loader2, PenSquare, Send, ChevronLeft, ChevronDown, Check } from "lucide-react";
import { GROWTHOS_BASE } from "../config/api";

/* ==========================================================
   NOTIFICATION CENTER
   Bell icon + badge, a click-to-open dropdown with the full
   notification history, and toast popups that auto-dismiss
   after ~3.5s for anything new since the last poll.

   mode="company" (default): GET/POST /notifications... — scoped
   server-side (get_current_company) to the logged-in company's
   own notifications only. This is where "Admin approved your
   lead's profile" shows up.

   mode="admin": GET/POST /admin/notifications... — the
   SuperAdmin's own feed. This is where "Veltrix Solutions
   approved lead Vaibhavi's profile" shows up.

   Same API_BASE/GROWTHOS/token convention Companyapprovals.js
   already uses — self-contained on purpose so this drops into
   either the company dashboard or the SuperAdmin panel with no
   other imports required.
========================================================== */
//const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const GROWTHOS = `${GROWTHOS_BASE}`;

const apiFetch = async (path, options = {}, isAdmin = false) => {
    const token = isAdmin
        ? localStorage.getItem("growthos_admin_token") || localStorage.getItem("growthos_token")
        : localStorage.getItem("growthos_token");
    const res = await fetch(`${GROWTHOS}${path}`, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(options.headers || {}),
        },
    });
    let body = null;
    try {
        body = await res.json();
    } catch {
        // no JSON body — leave as null
    }
    if (!res.ok) {
        throw new Error(body?.detail || body?.message || `Request failed (${res.status})`);
    }
    return body;
};

const fetchNotifications = (isAdmin) => apiFetch(isAdmin ? "/admin/notifications" : "/notifications", {}, isAdmin);

const markOneRead = (id, isAdmin) =>
    apiFetch(isAdmin ? `/admin/notifications/${id}/read` : `/notifications/${id}/read`, { method: "POST" }, isAdmin);

const markAllRead = (isAdmin) =>
    apiFetch(isAdmin ? "/admin/notifications/read-all" : "/notifications/read-all", { method: "POST" }, isAdmin);

// Compose-and-send. isAdmin: SuperAdmin -> a specific company or every
// company. Company: owner/teammate -> a specific teammate or the whole
// team. Either way this hits the "company" audience server-side — a
// company session can never send to the admin feed.
const sendNotification = (payload, isAdmin) =>
    apiFetch(isAdmin ? "/admin/notifications/send" : "/notifications/send", {
        method: "POST",
        body: JSON.stringify(payload),
    }, isAdmin);

// Recipient pickers: SuperAdmin picks from every registered company;
// a company picks from its own team roster.
const fetchCompanyList = () => apiFetch("/admin/companies", {}, true);
const fetchTeamRoster = () => apiFetch("/team-members", {}, false);

const TOAST_LIFETIME_MS = 3500; // "pop up for 3 to 4 sec"
const POLL_INTERVAL_MS = 20000; // re-check for anything new every 20s

const timeAgo = (raw) => {
    if (!raw) return "";
    const d = new Date(String(raw).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
};

/* ==========================================================
   THEMED SELECT
   A native <select>'s dropdown list is drawn by the OS/browser
   chrome, not the page — no amount of Tailwind on the <select>
   itself can theme it, which is why it was showing up as a
   plain white/blue list. This is a fully custom, on-theme
   replacement (dark panel, orange highlight) built the same
   way the notification dropdown itself is: a trigger button +
   an absolutely-positioned menu, closed on outside click.
========================================================== */
const ThemedSelect = ({ value, onChange, options, placeholder = "Select…", disabled = false }) => {
    const [open, setOpen] = useState(false);
    const selected = options.find((o) => String(o.value) === String(value));

    return (
        <div className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-2 rounded-lg bg-white/[0.04] border border-orange-600/20 text-sm text-left px-2.5 py-2 outline-none transition hover:border-orange-500/40 focus:border-orange-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <span className={selected ? "text-white truncate" : "text-gray-500 truncate"}>
                    {selected ? selected.label : placeholder}
                </span>
                <ChevronDown size={14} className={`text-gray-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <>
                    <div className="fixed inset-0 z-[1998]" onClick={() => setOpen(false)} />
                    <div className="absolute left-0 right-0 mt-1.5 max-h-52 overflow-y-auto rounded-lg border border-orange-600/20 bg-[#161618] shadow-[0_10px_30px_-8px_rgba(0,0,0,0.7)] z-[1999] py-1">
                        {options.length === 0 && (
                            <div className="px-3 py-2 text-xs text-gray-500">No options available</div>
                        )}
                        {options.map((o) => {
                            const isSelected = String(o.value) === String(value);
                            return (
                                <button
                                    type="button"
                                    key={o.value}
                                    onClick={() => {
                                        onChange(o.value);
                                        setOpen(false);
                                    }}
                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition ${
                                        isSelected ? "bg-orange-500/15 text-orange-400" : "text-gray-200 hover:bg-white/5"
                                    }`}
                                >
                                    <span className="truncate">{o.label}</span>
                                    {isSelected && <Check size={13} className="shrink-0" />}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

const NotificationCenter = ({ mode = "company" }) => {
    const isAdmin = mode === "admin";

    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toasts, setToasts] = useState([]);

    // ── Compose panel (send a notification from inside the bell) ──
    // recipients: admin -> every company on the platform (for the "send
    // to one company" picker); company -> this company's own team
    // roster (for the "send to one teammate" picker). Loaded lazily,
    // only the first time the compose panel is opened.
    const [composing, setComposing] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [recipientsLoaded, setRecipientsLoaded] = useState(false);
    const [recipientsLoading, setRecipientsLoading] = useState(false);
    const [sendTarget, setSendTarget] = useState("all"); // "all" | "company"/"member"
    const [sendTargetId, setSendTargetId] = useState("");
    const [sendTitle, setSendTitle] = useState("");
    const [sendMessage, setSendMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);

    // Highest notification id already accounted for. Starts unset so the
    // very first load — which may already contain notifications from
    // before this component ever mounted — never toast-storms the
    // screen; only ids newer than that first load actually pop a toast.
    const lastSeenIdRef = useRef(0);
    const initializedRef = useRef(false);

    const dismissToast = useCallback((key) => {
        setToasts((prev) => prev.filter((t) => t._key !== key));
    }, []);

    const pushToast = useCallback(
        (n) => {
            const key = `${n.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            setToasts((prev) => [...prev, { ...n, _key: key }]);
            setTimeout(() => dismissToast(key), TOAST_LIFETIME_MS);
        },
        [dismissToast]
    );

    const load = useCallback(
        async ({ silent = false } = {}) => {
            if (!silent) setLoading(true);
            setError(null);
            try {
                const res = await fetchNotifications(isAdmin);
                const rows = res?.data || [];
                setItems(rows);
                setUnreadCount(res?.unread_count ?? rows.filter((r) => !r.is_read).length);

                const newestId = rows.length ? Math.max(...rows.map((r) => r.id)) : 0;
                if (!initializedRef.current) {
                    // First load ever — just remember where we are, nothing to toast.
                    lastSeenIdRef.current = newestId;
                    initializedRef.current = true;
                } else if (newestId > lastSeenIdRef.current) {
                    const newOnes = rows.filter((r) => r.id > lastSeenIdRef.current);
                    // Oldest-first so toasts stack in the order they happened.
                    [...newOnes].reverse().forEach(pushToast);
                    lastSeenIdRef.current = newestId;
                }
            } catch (err) {
                setError(err.message || "Could not load notifications");
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [isAdmin, pushToast]
    );

    useEffect(() => {
        load();
        const interval = setInterval(() => load({ silent: true }), POLL_INTERVAL_MS);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const handleMarkAllRead = async () => {
        const prev = items;
        setItems((rs) => rs.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
        try {
            await markAllRead(isAdmin);
        } catch (err) {
            setItems(prev);
            setError(err.message || "Could not mark notifications read");
        }
    };

    const handleItemClick = async (n) => {
        if (n.is_read) return;
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
        setUnreadCount((c) => Math.max(0, c - 1));
        try {
            await markOneRead(n.id, isAdmin);
        } catch {
            // non-fatal — local read state already applied optimistically
        }
    };

    // Lazy-load the recipient list the first time compose opens — admin
    // gets every company, a company gets its own team roster. Cached
    // after that (skip re-fetching on repeated opens).
    const openCompose = async () => {
        setComposing(true);
        setSendError(null);
        if (recipientsLoaded) return;
        setRecipientsLoading(true);
        try {
            const res = isAdmin ? await fetchCompanyList() : await fetchTeamRoster();
            setRecipients(res?.data || []);
            setRecipientsLoaded(true);
        } catch (err) {
            setSendError(err.message || "Could not load recipient list");
        } finally {
            setRecipientsLoading(false);
        }
    };

    const closeCompose = () => {
        setComposing(false);
        setSendError(null);
        setSendTarget("all");
        setSendTargetId("");
        setSendTitle("");
        setSendMessage("");
    };

    const handleSend = async () => {
        if (!sendTitle.trim() || !sendMessage.trim()) {
            setSendError("Title and message are both required");
            return;
        }
        const targetMode = sendTarget === "all" ? "all" : isAdmin ? "company" : "member";
        if (targetMode !== "all" && !sendTargetId) {
            setSendError(isAdmin ? "Pick a company to send to" : "Pick a team member to send to");
            return;
        }
        setSending(true);
        setSendError(null);
        try {
            await sendNotification(
                {
                    title: sendTitle.trim(),
                    message: sendMessage.trim(),
                    target: targetMode,
                    target_id: targetMode === "all" ? null : Number(sendTargetId),
                },
                isAdmin
            );
            closeCompose();
            load(); // pull the new row straight into "sent" state — refreshes list/badge
        } catch (err) {
            setSendError(err.message || "Could not send notification");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {/* ================= BELL + BADGE ================= */}
            <div className="relative">
                <button
                    onClick={() => setOpen((o) => !o)}
                    className="relative h-10 w-10 rounded-xl border border-orange-600/20 bg-white/[0.03] flex items-center justify-center text-gray-300 hover:text-orange-500 hover:bg-white/5 transition"
                    title="Notifications"
                >
                    <Bell size={18} />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 h-5 min-w-[1.25rem] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">
                            {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                    )}
                </button>

                {/* ================= DROPDOWN — full history ================= */}
                {open && (
                    <>
                        <div className="fixed inset-0 z-[1996]" onClick={() => setOpen(false)} />
                        <div className="absolute right-0 mt-2 w-80 max-h-[70vh] rounded-2xl border border-orange-600/20 bg-[#0d0d10] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)] z-[1997] flex flex-col overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-orange-600/10 shrink-0">
                                {composing ? (
                                    <button
                                        onClick={closeCompose}
                                        className="flex items-center gap-1 text-white font-bold text-sm hover:text-orange-400 transition"
                                    >
                                        <ChevronLeft size={15} />
                                        New notification
                                    </button>
                                ) : (
                                    <p className="text-white font-bold text-sm">Notifications</p>
                                )}
                                {!composing && (
                                    <div className="flex items-center gap-3">
                                        {unreadCount > 0 && (
                                            <button
                                                onClick={handleMarkAllRead}
                                                className="flex items-center gap-1 text-[11px] text-orange-400 hover:text-orange-300 transition"
                                            >
                                                <CheckCheck size={13} />
                                                Mark all read
                                            </button>
                                        )}
                                        <button
                                            onClick={openCompose}
                                            title={isAdmin ? "Send to a company" : "Send to your team"}
                                            className="flex items-center gap-1 text-[11px] text-orange-400 hover:text-orange-300 transition"
                                        >
                                            <PenSquare size={13} />
                                            New
                                        </button>
                                    </div>
                                )}
                            </div>

                            {composing && (
                                <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 flex flex-col gap-3">
                                    {/* Recipient scope: admin picks a company or "all companies";
                                        a company picks a teammate or "whole team". */}
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[11px] text-gray-500">Send to</label>
                                        <ThemedSelect
                                            value={sendTarget}
                                            onChange={(val) => {
                                                setSendTarget(val);
                                                setSendTargetId("");
                                            }}
                                            options={[
                                                { value: "all", label: isAdmin ? "All companies" : "Whole team" },
                                                {
                                                    value: isAdmin ? "company" : "member",
                                                    label: isAdmin ? "One company…" : "One team member…",
                                                },
                                            ]}
                                        />
                                    </div>

                                    {sendTarget !== "all" && (
                                        <div className="flex flex-col gap-1.5">
                                            {recipientsLoading ? (
                                                <div className="flex items-center gap-2 text-xs text-gray-500 py-1">
                                                    <Loader2 size={13} className="animate-spin" />
                                                    Loading {isAdmin ? "companies" : "team"}…
                                                </div>
                                            ) : (
                                                <ThemedSelect
                                                    value={sendTargetId}
                                                    onChange={(val) => setSendTargetId(val)}
                                                    placeholder={isAdmin ? "Select a company…" : "Select a team member…"}
                                                    options={recipients.map((r) => ({
                                                        value: r.id,
                                                        label: isAdmin
                                                            ? r.company_name
                                                            : `${r.name}${r.email ? ` (${r.email})` : ""}`,
                                                    }))}
                                                />
                                            )}
                                        </div>
                                    )}

                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[11px] text-gray-500">Title</label>
                                        <input
                                            value={sendTitle}
                                            onChange={(e) => setSendTitle(e.target.value)}
                                            maxLength={200}
                                            placeholder="e.g. Scheduled maintenance tonight"
                                            className="w-full rounded-lg bg-white/[0.04] border border-orange-600/20 text-sm text-white placeholder:text-gray-600 px-2.5 py-2 outline-none focus:border-orange-500/50"
                                        />
                                    </div>

                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[11px] text-gray-500">Message</label>
                                        <textarea
                                            value={sendMessage}
                                            onChange={(e) => setSendMessage(e.target.value)}
                                            maxLength={500}
                                            rows={4}
                                            placeholder="Type your message…"
                                            className="w-full resize-none rounded-lg bg-white/[0.04] border border-orange-600/20 text-sm text-white placeholder:text-gray-600 px-2.5 py-2 outline-none focus:border-orange-500/50"
                                        />
                                    </div>

                                    {sendError && <div className="text-xs text-red-400">{sendError}</div>}

                                    <button
                                        onClick={handleSend}
                                        disabled={sending}
                                        className="mt-1 w-full flex items-center justify-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 transition"
                                    >
                                        {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                        {sending ? "Sending…" : "Send"}
                                    </button>
                                </div>
                            )}

                            {!composing && (
                            <div className="flex-1 min-h-0 overflow-y-auto">
                                {loading && (
                                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-8">
                                        <Loader2 size={15} className="animate-spin" />
                                        Loading…
                                    </div>
                                )}
                                {!loading && error && <div className="px-4 py-6 text-sm text-red-400">{error}</div>}
                                {!loading && !error && items.length === 0 && (
                                    <div className="px-4 py-10 text-center text-sm text-gray-500">No notifications yet.</div>
                                )}
                                {!loading && !error && items.length > 0 && (
                                    <ul>
                                        {items.map((n) => (
                                            <li
                                                key={n.id}
                                                onClick={() => handleItemClick(n)}
                                                className={`px-4 py-3 border-b border-orange-600/5 cursor-pointer transition hover:bg-white/[0.03] ${
                                                    n.is_read ? "" : "bg-orange-500/[0.04]"
                                                }`}
                                            >
                                                <div className="flex items-start gap-2">
                                                    {!n.is_read && (
                                                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-orange-500 shrink-0" />
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="text-sm text-white font-medium truncate">{n.title}</p>
                                                        <p className="text-xs text-gray-400 mt-0.5">{n.message}</p>
                                                        <p className="text-[10px] text-gray-600 mt-1">
                                                            {n.sender_label ? `${n.sender_label} · ` : ""}
                                                            {timeAgo(n.created_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* ================= TOASTS — auto-dismiss after ~3.5s ================= */}
            <div className="fixed top-4 right-4 z-[2000] flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]">
                {toasts.map((t) => (
                    <div
                        key={t._key}
                        className="rounded-xl border border-orange-500/30 bg-[#0d0d10] shadow-[0_10px_30px_-8px_rgba(0,0,0,0.7)] px-4 py-3 flex items-start gap-2.5"
                    >
                        <Bell size={15} className="text-orange-400 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm text-white font-semibold truncate">{t.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{t.message}</p>
                        </div>
                        <button onClick={() => dismissToast(t._key)} className="text-gray-500 hover:text-white shrink-0">
                            <X size={14} />
                        </button>
                    </div>
                ))}
            </div>
        </>
    );
};

export default NotificationCenter;